import { useTimezoneManager } from '@sportninja/common/hooks/useTimezoneManager'

interface UseEventRowTimesArgs {
  /** ISO 8601 UTC instant. */
  startsAt: string
  /** The event's IANA zone. Optional on an event, unlike a game's venue. */
  timezone?: string | null
}

interface UseEventRowTimesReturn {
  /** Viewer-local, e.g. 'August 05, 2026 - 7:00 PM (PDT)'. */
  viewerTime: string
  /**
   * Event-local, e.g. '10:00 PM EDT' — or '' when the two zones share an
   * offset at this instant and a second line would just repeat the first.
   */
  eventTime: string
}

const VIEWER_FORMAT = 'MMMM dd, y - h:mm a (ZZZZ)'

/**
 * The two times an event row shows, mirroring what a game row does with its
 * venue timezone: viewer-local on top, event-local beneath when they differ.
 *
 * Two differences from the game path, both driven by events being laxer:
 *
 * - `useTimezoneManager` computes nothing unless BOTH a date and a timezone are
 *   passed, so a game with no venue zone renders no time at all. An event's
 *   `timezone` is genuinely optional, so the viewer's own zone is substituted
 *   when it is absent — the second line then correctly collapses, since the
 *   zones are identical by construction.
 * - The hook's same-zone check compares ISO strings, i.e. UTC offsets rather
 *   than zone names. Two zones that share an offset at this instant
 *   (America/Toronto vs America/New_York) collapse to one line. That matches
 *   the games list, so a mixed list stays consistent.
 */
export const useEventRowTimes = ({
  startsAt,
  timezone,
}: UseEventRowTimesArgs): UseEventRowTimesReturn => {
  'use memo'

  const resolvedTimezone =
    timezone || Intl.DateTimeFormat().resolvedOptions().timeZone

  const {
    userDate,
    locationDate,
    checkLocationDateAndUserDateAreSame,
    getDateString,
  } = useTimezoneManager({ date: startsAt, timezone: resolvedTimezone })

  const viewerTime = userDate ? getDateString(userDate, VIEWER_FORMAT) : ''

  const showEventTime = Boolean(
    locationDate && !checkLocationDateAndUserDateAreSame()
  )

  // Prepend the date only when the two zones land on different calendar days,
  // otherwise the time alone is enough context.
  const eventTime = showEventTime
    ? getDateString(
        locationDate,
        userDate?.toFormat?.('dd') !== locationDate?.toFormat?.('dd')
          ? 'MMM d - h:mm a ZZZZ'
          : 'h:mm a ZZZZ'
      )
    : ''

  return { viewerTime, eventTime }
}
